import { DownloadOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type {
  CategoryRow,
  MaterialImportItem,
  MaterialImportResult,
  MaterialRow,
  MaterialSaveRequest,
  UnitRow
} from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMemo, useState } from 'react';
import { sumWidth, TipText } from '@hq/ui-kit';
import { hq } from '../session';

const PAGE_SIZE = 20;

/** 批量导入模板表头（与 `parseImportCsv` 的列序一致，可从 Excel 直接复制）。 */
const CSV_HEADER =
  '物资名称,分类编码,基本单位编码,采购单位编码,换算比,规格,品牌,型号,参考价,起订量,医疗器械,应急物资,批号管理,效期管理,有效期天数,备注';

const CSV_TEMPLATE = `${CSV_HEADER}\n一次性使用无菌注射器,C-MED,BOX,BOX,1,5ml,康德莱,KDL-5ML,1.25,10,否,否,是,是,1095,示例行（导入前请删除）`;

/** 物资管理类别（`wl_material.manage_type`）。 */
const MANAGE_TYPE_META: Record<string, { text: string; color: string }> = {
  NORMAL: { text: '普通', color: 'default' },
  COLD_CHAIN: { text: '冷链', color: 'blue' },
  STERILE: { text: '无菌', color: 'purple' },
  HAZARD: { text: '危化', color: 'orange' }
};

/** 管控等级（`wl_material.control_level`）。 */
const CONTROL_LEVEL_META: Record<string, { text: string; color: string }> = {
  NONE: { text: '不管控', color: 'default' },
  QUOTA: { text: '定额', color: 'geekblue' },
  STRICT: { text: '严控', color: 'red' }
};

/** 默认货源策略（`wl_material.default_source_mode`）。 */
const SOURCE_MODE_META: Record<string, string> = {
  AUTO: '自动判源',
  INNER_ONLY: '仅院内',
  OUTER_FORCE: '强制外仓'
};

interface MaterialFormValues {
  materialName: string;
  categoryId?: string;
  alias?: string;
  brand?: string;
  model?: string;
  spec?: string;
  baseUnitId?: string;
  purchaseUnitId?: string;
  packRate?: number;
  moq?: number;
  referencePrice?: number;
  manageType?: string;
  controlLevel?: string;
  defaultSourceMode?: string;
  isMedicalDevice?: boolean;
  isEmergency?: boolean;
  needBatch?: boolean;
  needExpiry?: boolean;
  shelfLifeDays?: number;
  remark?: string;
}

/** 表单值 → 保存报文。`materialCode` 不提交：编码由服务端按「M-分类段-4 位序号」生成。 */
function buildBody(id: string | undefined, values: MaterialFormValues): MaterialSaveRequest {
  return {
    id,
    materialName: values.materialName.trim(),
    alias: values.alias?.trim() || null,
    categoryId: values.categoryId ?? '0',
    brand: values.brand?.trim() || null,
    model: values.model?.trim() || null,
    spec: values.spec?.trim() || null,
    baseUnitId: values.baseUnitId ?? '0',
    purchaseUnitId: values.purchaseUnitId ?? '0',
    packRate: values.packRate ?? 1,
    moq: values.moq ?? 0,
    referencePrice: values.referencePrice ?? 0,
    isMedicalDevice: Boolean(values.isMedicalDevice),
    manageType: values.manageType ?? 'NORMAL',
    controlLevel: values.controlLevel ?? 'NONE',
    isEmergency: Boolean(values.isEmergency),
    needBatch: Boolean(values.needBatch),
    needExpiry: Boolean(values.needExpiry),
    shelfLifeDays: values.shelfLifeDays ?? 0,
    defaultSourceMode: values.defaultSourceMode ?? 'AUTO',
    remark: values.remark?.trim() || null
  };
}

/**
 * 物资主数据维护（运营端）：列表 + 新建/编辑。
 * 编码由系统生成（`M-分类段-4 位序号`，如 `M-MED-0001`），建档后不可改；改分类不重编码。
 */
export default function MaterialOps() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const [keyword, setKeyword] = useState('');
  const [categoryId, setCategoryId] = useState<string | undefined>(undefined);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<MaterialRow | null>(null);
  const [form] = Form.useForm<MaterialFormValues>();

  const [importOpen, setImportOpen] = useState(false);
  const [importCsv, setImportCsv] = useState('');
  const [importResult, setImportResult] = useState<MaterialImportResult | null>(null);

  // 粘贴内容实时解析：有解析错误就不允许提交，避免把明显不合法的行发给后端
  const parsed = useMemo(() => {
    if (!importCsv.trim()) return { rows: [] as MaterialImportItem[], error: null as string | null };
    try {
      return { rows: parseImportCsv(importCsv), error: null as string | null };
    } catch (error) {
      return { rows: [] as MaterialImportItem[], error: (error as Error).message };
    }
  }, [importCsv]);

  const list = useInfiniteQuery({
    queryKey: ['ops', 'material', 'list', keyword, categoryId],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.material.list({
        lastId: pageParam,
        size: PAGE_SIZE,
        keyword: keyword || undefined,
        categoryId
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const categories = useQuery({
    queryKey: ['ops', 'material', 'categories'],
    placeholderData: keepPreviousData,
    queryFn: () => hq.catalog.categories({}),
    staleTime: 5 * 60 * 1000
  });

  const units = useQuery({
    queryKey: ['ops', 'material', 'units'],
    placeholderData: keepPreviousData,
    queryFn: () => hq.catalog.units({ size: 200 }),
    staleTime: 5 * 60 * 1000
  });

  const save = useMutation({
    mutationFn: (payload: { id?: string; values: MaterialFormValues }) =>
      hq.material.save(buildBody(payload.id, payload.values)),
    onSuccess: async (result) => {
      message.success(result.created ? `已建档，编码 ${result.materialCode ?? '-'}` : '物资已更新');
      setFormOpen(false);
      setEditing(null);
      form.resetFields();
      await queryClient.invalidateQueries({ queryKey: ['ops', 'material'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败，请稍后重试');
    }
  });

  const importMaterials = useMutation({
    mutationFn: (items: MaterialImportItem[]) => hq.material.import({ items }),
    onSuccess: async (result) => {
      setImportResult(result);
      if (result.failedCount === 0) {
        message.success(`导入完成：${result.successCount} 行`);
        setImportOpen(false);
        setImportCsv('');
      } else {
        message.warning(`导入完成：成功 ${result.successCount} 行，失败 ${result.failedCount} 行`);
      }
      await queryClient.invalidateQueries({ queryKey: ['ops', 'material'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '导入失败，请稍后重试');
    }
  });

  const openImport = () => {
    setImportCsv('');
    setImportResult(null);
    setImportOpen(true);
  };

  const submitImport = () => {
    if (parsed.error) {
      message.error(parsed.error);
      return;
    }
    if (parsed.rows.length === 0) {
      message.error('请先粘贴导入数据');
      return;
    }
    setImportResult(null);
    importMaterials.mutate(parsed.rows);
  };

  const downloadTemplate = () => {
    const blob = new Blob([`\uFEFF${CSV_TEMPLATE}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = '物资主数据导入模板.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const openCreate = () => {
    setEditing(null);
    form.setFieldsValue({
      manageType: 'NORMAL',
      controlLevel: 'NONE',
      defaultSourceMode: 'AUTO',
      packRate: 1,
      moq: 0,
      referencePrice: 0,
      shelfLifeDays: 0,
      needBatch: false,
      needExpiry: false,
      isMedicalDevice: false,
      isEmergency: false
    });
    setFormOpen(true);
  };

  const openEdit = (row: MaterialRow) => {
    setEditing(row);
    form.setFieldsValue({
      materialName: row.materialName ?? '',
      categoryId: row.categoryId === '0' ? undefined : row.categoryId,
      alias: row.alias ?? undefined,
      brand: row.brand ?? undefined,
      model: row.model ?? undefined,
      spec: row.spec ?? undefined,
      baseUnitId: row.baseUnitId === '0' ? undefined : row.baseUnitId,
      purchaseUnitId: row.purchaseUnitId === '0' ? undefined : row.purchaseUnitId,
      packRate: row.packRate,
      moq: row.moq,
      referencePrice: row.referencePrice,
      manageType: row.manageType ?? 'NORMAL',
      controlLevel: row.controlLevel ?? 'NONE',
      defaultSourceMode: row.defaultSourceMode ?? 'AUTO',
      isMedicalDevice: row.isMedicalDevice,
      isEmergency: row.isEmergency,
      needBatch: row.needBatch,
      needExpiry: row.needExpiry,
      shelfLifeDays: row.shelfLifeDays,
      remark: row.remark ?? undefined
    });
    setFormOpen(true);
  };

  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];
  const categoryOptions: CategoryRow[] = categories.data ?? [];
  const unitOptions: UnitRow[] = units.data?.items ?? [];

  const columns: ColumnsType<MaterialRow> = [
    {
      title: '物资编码',
      dataIndex: 'materialCode',
      width: 140,
      render: (value: string | null) => <Typography.Text code>{value ?? '-'}</Typography.Text>
    },
    {
      title: '物资名称',
      dataIndex: 'materialName',
      ellipsis: { showTitle: false },
      render: (value: string | null, row) => (
        <Space direction="vertical" size={0}>
          <TipText text={value ?? '-'} />
          {row.alias ? <Typography.Text type="secondary">{row.alias}</Typography.Text> : null}
        </Space>
      )
    },
    { title: '分类', dataIndex: 'categoryName', width: 140, render: (value: string | null) => value || '未分类' },
    {
      title: '规格 / 型号',
      dataIndex: 'spec',
      width: 180,
      ellipsis: { showTitle: false },
      render: (_value: string | null, row) => <TipText text={[row.spec, row.model].filter(Boolean).join(' / ') || '-'} />
    },
    { title: '品牌', dataIndex: 'brand', width: 120, render: (value: string | null) => value || '-' },
    {
      title: '单位',
      key: 'unit',
      width: 160,
      render: (_value: unknown, row) => (
        <Space direction="vertical" size={0}>
          <Typography.Text>{row.unitName ?? '-'}</Typography.Text>
          {row.purchaseUnitName ? (
            <Typography.Text type="secondary">
              1{row.purchaseUnitName}={row.packRate}
              {row.unitName ?? ''}
            </Typography.Text>
          ) : null}
        </Space>
      )
    },
    {
      title: '参考价',
      dataIndex: 'referencePrice',
      width: 110,
      align: 'right',
      render: (value: number) => formatAmount(value, 4)
    },
    {
      title: '管理类别',
      dataIndex: 'manageType',
      width: 100,
      render: (value: string | null) => {
        const meta = (value && MANAGE_TYPE_META[value]) || { text: value ?? '-', color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    {
      title: '管控',
      dataIndex: 'controlLevel',
      width: 90,
      render: (value: string | null) => {
        const meta = (value && CONTROL_LEVEL_META[value]) || { text: value ?? '-', color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    {
      title: '属性',
      key: 'flags',
      width: 200,
      render: (_value: unknown, row) => (
        <Space size={4} wrap>
          {row.isMedicalDevice ? <Tag>医疗器械</Tag> : null}
          {row.isEmergency ? <Tag color="red">应急</Tag> : null}
          {row.needBatch ? <Tag color="blue">批号</Tag> : null}
          {row.needExpiry ? <Tag color="gold">效期</Tag> : null}
          {row.defaultSourceMode && row.defaultSourceMode !== 'AUTO' ? (
            <Tag color="cyan">{SOURCE_MODE_META[row.defaultSourceMode] ?? row.defaultSourceMode}</Tag>
          ) : null}
          {!row.isMedicalDevice && !row.isEmergency && !row.needBatch && !row.needExpiry ? (
            <Typography.Text type="secondary">-</Typography.Text>
          ) : null}
        </Space>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 90,
      fixed: 'right',
      render: (_value: unknown, row) => (
        <Button type="link" onClick={() => openEdit(row)}>
          编辑
        </Button>
      )
    }
  ];

  return (
    <Card
      className="page-card"
      title="物资主数据"
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} loading={list.isFetching} onClick={() => void list.refetch()}>
            刷新
          </Button>
          <Button icon={<DownloadOutlined />} onClick={openImport}>
            批量导入
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            新建物资
          </Button>
        </Space>
      }
    >
      <Typography.Paragraph type="secondary">
        物资编码由系统按「M-分类段-4 位序号」自动生成（如 `M-MED-0001`，未分类走 `GEN` 段），建档后不可修改；后续调整分类不会重编码。
      </Typography.Paragraph>

      <Space wrap style={{ marginBottom: 12 }}>
        <Input.Search
          allowClear
          placeholder="按名称 / 编码 / 检索词搜索"
          style={{ width: 280 }}
          onSearch={(value) => setKeyword(value.trim())}
          enterButton
        />
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="按分类筛选"
          style={{ width: 200 }}
          value={categoryId}
          onChange={(value) => setCategoryId(value)}
          options={categoryOptions.map((item) => ({ value: item.id, label: `${item.categoryCode} ${item.categoryName}` }))}
        />
      </Space>

      {list.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="物资列表加载失败"
          description={list.error instanceof ApiError ? list.error.friendlyMessage : String(list.error)}
        />
      ) : null}

      <Table<MaterialRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={list.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: '暂无物资' }}
        onRow={(row) => ({ onDoubleClick: () => openEdit(row) })}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {list.hasNextPage ? (
          <Button onClick={() => list.fetchNextPage()} loading={list.isFetchingNextPage}>
            加载更多
          </Button>
        ) : null}
      </Space>

      <Modal
        title={editing ? `维护物资：${editing.materialCode ?? editing.id}` : '新建物资'}
        open={formOpen}
        width={880}
        destroyOnHidden
        confirmLoading={save.isPending}
        onCancel={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onOk={async () => {
          const values = await form.validateFields();
          save.mutate({ id: editing?.id, values });
        }}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="materialName"
                label="物资名称"
                rules={[
                  { required: true, message: '请填写物资名称' },
                  { max: 255, message: '不超过 255 字' }
                ]}
              >
                <Input placeholder="如：一次性使用无菌注射器" maxLength={255} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="alias" label="别名 / 检索词">
                <Input placeholder="选填，便于检索" maxLength={255} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="categoryId" label="分类" extra="留空为未分类；编码分类段在建档时确定，之后改分类不重编码">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="选择分类"
                  options={categoryOptions.map((item) => ({
                    value: item.id,
                    label: `${item.categoryCode} ${item.categoryName}`
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="brand" label="品牌">
                <Input maxLength={128} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="spec" label="规格">
                <Input maxLength={255} placeholder="如：5ml" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="model" label="型号">
                <Input maxLength={128} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="baseUnitId" label="基本单位" rules={[{ required: true, message: '请选择基本单位' }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="选择单位"
                  options={unitOptions.map((item) => ({ value: item.id, label: `${item.unitCode} ${item.unitName}` }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="purchaseUnitId" label="采购单位" extra="留空视为与基本单位一致">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="选择采购单位"
                  options={unitOptions.map((item) => ({ value: item.id, label: `${item.unitCode} ${item.unitName}` }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="packRate" label="换算比" extra="1 采购单位 = N 基本单位，小于 1 按 1 处理">
                <InputNumber min={1} precision={4} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="moq" label="起订量">
                <InputNumber min={0} precision={3} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="referencePrice" label="参考价（元）">
                <InputNumber min={0} precision={4} style={{ width: '100%' }} addonBefore="¥" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="shelfLifeDays" label="有效期天数">
                <InputNumber min={0} precision={0} style={{ width: '100%' }} addonAfter="天" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="manageType" label="管理类别">
                <Select
                  options={Object.entries(MANAGE_TYPE_META).map(([value, meta]) => ({ value, label: meta.text }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="controlLevel" label="管控等级">
                <Select
                  options={Object.entries(CONTROL_LEVEL_META).map(([value, meta]) => ({ value, label: meta.text }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="defaultSourceMode" label="默认货源策略">
                <Select
                  options={Object.entries(SOURCE_MODE_META).map(([value, label]) => ({ value, label }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="isMedicalDevice" label="医疗器械" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="isEmergency" label="应急物资" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="needBatch" label="批号管理" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="needExpiry" label="效期管理" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="批量导入物资主数据"
        open={importOpen}
        width={900}
        okText="开始导入"
        confirmLoading={importMaterials.isPending}
        destroyOnHidden
        onCancel={() => setImportOpen(false)}
        onOk={submitImport}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="从 Excel 复制数据直接粘贴；分类/单位按编码匹配，物资编码仍由系统生成"
          action={
            <Button size="small" icon={<DownloadOutlined />} onClick={downloadTemplate}>
              下载模板
            </Button>
          }
        />

        <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
          列顺序：{CSV_HEADER}
        </Typography.Paragraph>

        <Input.TextArea
          rows={10}
          value={importCsv}
          onChange={(event) => setImportCsv(event.target.value)}
          placeholder={`${CSV_HEADER}\n${CSV_TEMPLATE.split('\n')[1] ?? ''}`}
        />

        {parsed.error ? (
          <Alert type="error" showIcon style={{ marginTop: 12 }} message="解析失败" description={parsed.error} />
        ) : parsed.rows.length > 0 ? (
          <>
            <Typography.Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
              已识别 {parsed.rows.length} 行，预览前 5 行
            </Typography.Text>
            <Table<MaterialImportItem>
              rowKey={(row) => String(row.rowNo)}
              size="small"
              style={{ marginTop: 8 }}
              pagination={false}
              dataSource={parsed.rows.slice(0, 5)}
              columns={[
                { title: '行号', dataIndex: 'rowNo', width: 60 },
                { title: '物资名称', dataIndex: 'materialName' },
                {
                  title: '分类编码',
                  dataIndex: 'categoryCode',
                  width: 110,
                  render: (value: string | null) => value || '未分类'
                },
                { title: '基本单位', dataIndex: 'baseUnitCode', width: 100 },
                {
                  title: '参考价',
                  dataIndex: 'referencePrice',
                  width: 100,
                  align: 'right',
                  render: (value: number) => formatAmount(value, 4)
                }
              ]}
            />
          </>
        ) : null}

        {importResult ? (
          <Alert
            type={importResult.failedCount > 0 ? 'warning' : 'success'}
            showIcon
            style={{ marginTop: 12 }}
            message={`导入结果：成功 ${importResult.successCount} 行，失败 ${importResult.failedCount} 行`}
            description={
              importResult.errors.length > 0 ? (
                <Table
                  rowKey={(row) => String(row.rowNo)}
                  size="small"
                  pagination={false}
                  dataSource={importResult.errors}
                  columns={[
                    { title: '行号', dataIndex: 'rowNo', width: 60 },
                    { title: '原因', dataIndex: 'message' }
                  ]}
                />
              ) : null
            }
          />
        ) : null}
      </Modal>
    </Card>
  );
}

/** 解析粘贴的 CSV：首行有表头则跳过；空行忽略；缺名称/单位或数值非法时带行号报错。 */
function parseImportCsv(text: string): MaterialImportItem[] {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length === 0) throw new Error('请粘贴导入数据');

  const start = /^(物资名称|materialName)/i.test(lines[0] ?? '') ? 1 : 0;
  if (lines.length <= start) throw new Error('缺少数据行');

  return lines.slice(start).map((line, index) => {
    const rowNo = index + start + 1;
    const cells = line.split(',').map((cell) => cell.trim());
    const name = cells[0] ?? '';
    const baseUnitCode = cells[2] ?? '';
    if (!name) throw new Error(`第 ${rowNo} 行缺少物资名称`);
    if (!baseUnitCode) throw new Error(`第 ${rowNo} 行缺少基本单位编码`);

    return {
      rowNo,
      materialName: name,
      categoryCode: cells[1] || null,
      baseUnitCode,
      purchaseUnitCode: cells[3] || null,
      packRate: parseImportNumber(cells[4], rowNo, '换算比', 1),
      spec: cells[5] || null,
      brand: cells[6] || null,
      model: cells[7] || null,
      referencePrice: parseImportNumber(cells[8], rowNo, '参考价', 0),
      moq: parseImportNumber(cells[9], rowNo, '起订量', 0),
      isMedicalDevice: parseImportFlag(cells[10]),
      isEmergency: parseImportFlag(cells[11]),
      needBatch: parseImportFlag(cells[12]),
      needExpiry: parseImportFlag(cells[13]),
      shelfLifeDays: Math.trunc(parseImportNumber(cells[14], rowNo, '有效期天数', 0)),
      remark: cells[15] || null
    };
  });
}

function parseImportNumber(raw: string | undefined, rowNo: number, label: string, fallback: number): number {
  const value = (raw ?? '').trim();
  if (!value) return fallback;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) throw new Error(`第 ${rowNo} 行${label}非法`);
  return parsed;
}

/** 是/否单元格：`是`、`Y/YES`、`1`、`TRUE` 视为真，其余（含空）为假。 */
function parseImportFlag(raw: string | undefined): boolean {
  const value = (raw ?? '').trim().toLowerCase();
  return value === '是' || value === 'y' || value === 'yes' || value === '1' || value === 'true';
}
